import apiClient from './apiClient';

export interface DashboardMetrics {
  users: {
    total_users?: number;
    total_clients: number;
    total_artisans: number;
    total_admins?: number;
    verified_artisans: number;
    new_users_week: number;
  };
  jobs: {
    total: number;
    pending?: number;
    accepted?: number;
    in_progress?: number;
    active: number;
    completed: number;
    cancelled?: number;
    disputed?: number;
    completion_rate: number;
  };
  revenue: {
    today: number;
    week: number;
    month: number;
  };
  disputes: {
    open: number;
    resolved: number;
  };
  verifications: {
    pending: number;
    approved: number;
  };
}

export interface Activity {
  id: string;
  uuid?: string;           // globally unique ID from backend (preferred dedup key)
  type: string;            // open string — backend may add new types at any time
  title: string;
  description: string;
  timestamp: string;
  status: string;
  icon_type: string;       // open string — falls back to PeopleIcon for unknowns
  [key: string]: any;
}

export interface ActivityFeed {
  count: number;
  activities: Activity[];
}

class DashboardService {
  async getMetrics(): Promise<DashboardMetrics> {
    try {
      const response = await apiClient.get(`/admin/dashboard/`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching dashboard metrics:', error);
      throw error;
    }
  }

  /**
   * Get recent activities for the dashboard feed
   * Returns recent signups, verifications, jobs, payments, etc.
   * Handles both { activities: [] } and { results: [] } response shapes.
   */
  async getActivityFeed(limit: number = 10): Promise<ActivityFeed> {
    try {
      const response = await apiClient.get(`/admin/dashboard/activity/`, {
        params: { limit }
      });
      // Support multiple response shapes from Django:
      // { data: { activities: [...], count: N } }
      // { data: { results:    [...], count: N } }
      // { data: [...] }  (bare array)
      const payload = response.data?.data ?? response.data;
      const activities: Activity[] = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.activities)
          ? payload.activities
          : Array.isArray(payload?.results)
            ? payload.results
            : [];
      const count: number = payload?.count ?? payload?.total ?? activities.length;
      return { count, activities };
    } catch (error) {
      console.error('Error fetching dashboard activity:', error);
      throw error;
    }
  }
}

export default new DashboardService();
